import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWeddingLocation } from '../../context/WeddingLocationContext';
import { useWeddingVendors } from '../../hooks/useWeddingVendors';
import { useWeddingCategories } from '../../hooks/useWeddingCategories';
import { ArrowLeft, Search, MapPin } from 'lucide-react';
import VendorCard from '../../components/weddings/VendorCard';
import LocationPicker from '../../components/weddings/LocationPicker';

export default function VendorSearchPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const query = searchParams.get('q') || '';
    const [searchQuery, setSearchQuery] = useState(query); // Renamed from localQuery
    const [selectedCategory, setSelectedCategory] = useState('all');

    // Global Location Context
    const { location, setLocation } = useWeddingLocation();

    // Local radius state (doesn't strictly need to be global unless desired)
    const [radius, setRadius] = useState(50);

    const { data: categories = [] } = useWeddingCategories();
    const { data: vendors = [], isLoading } = useWeddingVendors({
        categorySlug: selectedCategory === 'all' ? undefined : selectedCategory,
        // Use Global Location
        lat: location?.lat,
        lon: location?.lon,
        radius: radius,
        city: location?.address // Optional, for text fallback if we wanted
    });

    useEffect(() => {
        setSearchQuery(query);
    }, [query]);

    const filteredVendors = vendors.filter((vendor) =>
        vendor.name.toLowerCase().includes(query.toLowerCase()) ||
        vendor.city?.toLowerCase().includes(query.toLowerCase()) ||
        categories.find(c => c.id === vendor.category_id)?.name.toLowerCase().includes(query.toLowerCase())
    );

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/weddings/search?q=${encodeURIComponent(searchQuery.trim())}`);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <button
                        onClick={() => navigate('/weddings')}
                        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
                    >
                        <ArrowLeft size={20} />
                        Back to Weddings
                    </button>

                    <form onSubmit={handleSearch} className="max-w-xl">
                        <div className="relative flex items-center mb-8">
                            <Search className="absolute left-4 text-gray-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search vendors..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-12 pr-24 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                            />
                            <button
                                type="submit"
                                className="absolute right-2 px-4 py-1.5 bg-rose-600 text-white rounded-md font-medium hover:bg-rose-700 transition-colors text-sm"
                            >
                                Search
                            </button>
                        </div>

                        {/* Location Filter */}
                        <div className="mb-8">
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                <MapPin size={18} />
                                Location
                            </h3>
                            <div className="space-y-4">
                                <LocationPicker
                                    onLocationSelect={(lat, lon, address) => {
                                        if (lat && lon) {
                                            setLocation({ lat, lon, address: address || '' });
                                        }
                                    }}
                                    initialAddress={location?.address}
                                />

                                {location && (
                                    <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600 dark:text-gray-400">Search Radius</span>
                                            <span className="font-medium text-gray-900 dark:text-white">{radius} km</span>
                                        </div>
                                        <input
                                            type="range"
                                            min="5"
                                            max="200"
                                            step="5"
                                            value={radius}
                                            onChange={(e) => setRadius(Number(e.target.value))}
                                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-600"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Recent/Featured Logic could go here */}
                    </form>

                    <p className="text-gray-600 dark:text-gray-400 mt-4">
                        {filteredVendors.length} results for "<span className="font-medium text-gray-900 dark:text-white">{query}</span>"
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-8">
                {isLoading ? (
                    <div className="text-center py-12 text-gray-500">Loading...</div>
                ) : filteredVendors.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-gray-500 dark:text-gray-400 text-lg">No vendors found matching your search.</p>
                        <button
                            onClick={() => navigate('/weddings')}
                            className="mt-4 text-rose-600 hover:text-rose-700 font-medium"
                        >
                            Browse all vendors
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {filteredVendors.map((vendor) => (
                            <div key={vendor.id} className="h-full">
                                <VendorCard vendor={vendor} />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
